import React, { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const CreateSiteModal = ({ isOpen, onClose, onSiteCreated }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    dns: '',
    status: 1,
    tarif: 0,
    seo_stat_url: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.dns.trim()) {
      setError(currentLang === 'ru' ? 'Пожалуйста, введите DNS' : 'Please enter DNS');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo/sites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      console.log('Create Site API Response:', data);

      if (response.ok && data.success) {
        console.log('Site created successfully');
        // Сбрасываем форму
        setFormData({
          dns: '',
          status: 1,
          tarif: 0,
          seo_stat_url: ''
        });
        
        // Закрываем модалку
        onClose();
        
        // Уведомляем родительский компонент
        if (onSiteCreated) {
          onSiteCreated();
        }
      } else {
        setError(data.message || (currentLang === 'ru' ? 'Ошибка создания сайта' : 'Error creating site'));
      }
    } catch (error) {
      console.error('Ошибка создания сайта:', error);
      setError(error.message || (currentLang === 'ru' ? 'Ошибка сети при создании сайта' : 'Network error while creating site'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        dns: '',
        status: 1,
        tarif: 0,
        seo_stat_url: ''
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">
              {currentLang === 'ru' ? 'Добавить новый сайт' : 'Add New Site'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* DNS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                DNS: *
              </label>
              <input
                type="text"
                value={formData.dns}
                onChange={(e) => handleInputChange('dns', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={currentLang === 'ru' ? 'example.com' : 'example.com'}
                disabled={loading}
                required
              />
            </div>

            {/* Статус */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLang === 'ru' ? 'Статус:' : 'Status:'}
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value={1}>{currentLang === 'ru' ? 'Активен' : 'Active'}</option>
                <option value={0}>{currentLang === 'ru' ? 'Неактивен' : 'Inactive'}</option>
              </select>
            </div>

            {/* Тариф */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLang === 'ru' ? 'Тариф:' : 'Tariff:'}
              </label>
              <select
                value={formData.tarif}
                onChange={(e) => handleInputChange('tarif', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              >
                <option value={0}>{currentLang === 'ru' ? 'Базовый' : 'Basic'}</option>
                <option value={1}>{currentLang === 'ru' ? 'Старт' : 'Start'}</option>
                <option value={2}>{currentLang === 'ru' ? 'Стандарт' : 'Standard'}</option>
                <option value={3}>{currentLang === 'ru' ? 'Бизнес' : 'Business'}</option>
                <option value={4}>{currentLang === 'ru' ? 'VIP' : 'VIP'}</option>
              </select>
            </div>
            {/* SEO Stat URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLang === 'ru' ? 'Url доступ к позициям' : 'SEO Stat URL'}
              </label>
              <input
                type="url"
                value={formData.seo_stat_url}
                onChange={(e) => handleInputChange('seo_stat_url', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={currentLang === 'ru' ? 'https://example.com/stat' : 'https://example.com/stat'}
                disabled={loading}
              />
            </div>

            {/* Кнопки действий */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                disabled={loading}
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{currentLang === 'ru' ? 'Создание...' : 'Creating...'}</span>
                  </div>
                ) : (
                  currentLang === 'ru' ? 'Создать' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSiteModal; 